import instances from '../config/axios';
import { AUTH_POC_URL, ENABLE_MOCKS } from '../constants/common';
import {
  invoicesList,
  invoiceById,
  registeredSolution,
  certificationCounts,
  invoicesCounts,
  OTPs,
  authorization,
  technicalFailures,
  specialBillingAgreements,
  pointsOfContact,
  terminateSpecialBillingAgreementsMock,
  activatePointOfContactMock,
  getSpecialAgreementInfoMock,
  getVatInfoMock,
  invoiceViewSales,
  invoiceViewSalesCounts,
  invoiceViewPurchases,
  invoiceViewPurchasesCounts,
  invoiceViewInvoiceList,
  invoiceViewThirdparty,
  invoiceViewThirdpartyStats,
  invoiceViewThirdpartyCounts,
  getStatementStatusMock,
} from './mocks';

export const generateOtp = async (vat, numberOfOTPs = 1) => {
  return ENABLE_MOCKS
    ? OTPs(numberOfOTPs)
    : instances.otpInstance.get(`/generate?otpCount=${numberOfOTPs}`, {
        headers: { vatNumber: vat },
      });
};

export const startProcess = () => {
  return instances.servicesInstance.post(
    '/onboarding/start',
    {},
    {
      headers: {
        callLogin: false,
      },
    },
  );
};

export const generateInvoice = (data, vatNumber) => {
  return instances.smeInstance.post('/invoices', data, {
    headers: {
      vatNumber,
    },
  });
};

export const getInvoicesList = async (taxnumber) => {
  return ENABLE_MOCKS
    ? invoicesList()
    : instances.smeInstance.get(`/invoices?vatNumber=${taxnumber}`, {
        headers: {
          taxnumber,
        },
      });
};

export const getInvoiceById = async (id, tax_number) => {
  return ENABLE_MOCKS
    ? invoiceById()
    : instances.smeInstance.get('/invoices/' + id, {
        headers: {
          tax_number,
        },
      });
};

export const getStatistics = async (tax_number) => {
  const certificationsNew = certificationCounts();
  const eInvoicingNew = invoicesCounts();
  const statistics = {
    data: {
      certifications: {
        ...certificationsNew?.data,
      },
      clearance: {
        ...eInvoicingNew?.data?.clearance,
      },
      reporting: {
        ...eInvoicingNew?.data?.reporting,
      },
    },
  };

  if (ENABLE_MOCKS) {
    return statistics;
  }

  const certifications = await instances.statisticsInstance.get(
    `/core/statistics/onboarding?vat=${tax_number}`,
  );
  const eInvoicing = await instances.statisticsInstance.get(
    `/core/statistics/invoiceStatistics?vat=${tax_number}`,
  );
  if (certifications && eInvoicing) {
    return {
      data: {
        certifications: {
          ...certifications?.data,
        },
        clearance: {
          ...eInvoicing?.data?.clearance,
        },
        reporting: {
          ...eInvoicing?.data?.reporting,
        },
      },
    };
  } else {
    throw new Error('');
  }
};

export const getInvoiceOnboardData = (tax_number, invoiceType) => {
  return instances.smeInstance.get(
    `/invoices/onboard/${tax_number}?invoiceType=${invoiceType}`,
  );
};

export const printInvoice = (invoiceId) => {
  return instances.smeInstance.get('/invoices/pdf/' + invoiceId, {
    responseType: 'blob',
  });
};

export const getRegisteredSolutions = async (taxNumber) => {
  return ENABLE_MOCKS
    ? Promise.resolve(registeredSolution())
    : instances.onboardingInstance
        .get('pcsids?vatNumber=' + taxNumber)
        .then((d) => {
          const mapResponse = (obj) => {
            Object.keys(obj).forEach((key) => {
              if (obj[key] === null) {
                obj[key] = '-';
              }
            });
            return obj;
          };
          if (d?.data?.productionCertificateResponseList) {
            d.data.productionCertificateResponseList =
              d?.data?.productionCertificateResponseList.map(mapResponse);
          }
          return d;
        });
};

export const getTechnicalFailureList = async (taxNumber) => {
  return ENABLE_MOCKS
    ? technicalFailures()
    : instances.servicesInstance.get(
        `/portal/notification/api/v1/failure-notifications/by-vat/${taxNumber}`,
      );
};

export const revokeCertificate = async (
  productionRequestIds,
  tax_number,
  reason,
  description,
) => {
  return instances.servicesInstance.delete('/core/onboarding/portal', {
    data: [
      ...productionRequestIds.map((productionRequestId) => ({
        productionRequestId: productionRequestId,
        reason: reason ?? 'OTHER',
        description: description ?? '',
      })),
    ],
    headers: {
      // 'Production-Request-Id': requestId,
      vatNumber: tax_number,
    },
  });
};

export const getInvoiceViewBySellerTaxId = (sellerTaxId) => {
  return instances.servicesInstance.get(
    `onboarding/serviceProvider/getAllTaxDataBySellerTaxId/${sellerTaxId}`,
  );
};

export const getInvoiceViewByBuyerTaxId = (buyerTaxId) => {
  return instances.servicesInstance.get(
    `onboarding/serviceProvider/getAllTaxDataByBuyerTaxId/${buyerTaxId}`,
  );
};

export const getUserAuthorization = async (taxNumber) => {
  return ENABLE_MOCKS
    ? authorization()
    : instances.smeInstance.get('csid/' + taxNumber);
};

export const authorizeWebApplication = async (taxNumber) => {
  return ENABLE_MOCKS
    ? authorization()
    : instances.smeInstance.post('csid/' + taxNumber);
};

export const getSpecialBillingList = async (taxNumber) => {
  return ENABLE_MOCKS
    ? specialBillingAgreements()
    : instances.servicesInstance.get('portal/self-billing/v1/agreements');
};

export const terminateSpecialBillingAgreements = async (agreements, reason) => {
  return ENABLE_MOCKS
    ? terminateSpecialBillingAgreementsMock()
    : instances.servicesInstance.delete(
        'portal/self-billing/v1/agreements/termination',
        {
          data: {
            items: agreements.map((agreement) => ({
              id: agreement.agreementId,
              reason: reason,
            })),
          },
        },
      );
};

export const deleteSpecialBillingAgreement = async (agreementId) => {
  return ENABLE_MOCKS
    ? terminateSpecialBillingAgreementsMock()
    : instances.servicesInstance.delete(
        `portal/self-billing/v1/agreements/deletion?agreementId=${agreementId}`,
      );
};

export const getPointsOfContact = async (taxPayerId, vatnumber) => {
  return ENABLE_MOCKS
    ? pointsOfContact()
    : instances.servicesInstance.get(
        `portal/notification/contacts?taxPayerId=${taxPayerId}`,
        { headers: { vatNumber: vatnumber } },
      );
};

export const addPointOfContact = async (pointOfContact, vatnumber) => {
  return instances.servicesInstance.post(
    'portal/notification/contacts',
    pointOfContact,
    { headers: { vatNumber: vatnumber } },
  );
};

export const deletePointOfContact = async (contactId, vatnumber) => {
  return instances.servicesInstance.delete(
    `portal/notification/contacts?contactId=${contactId}`,
    { headers: { vatNumber: vatnumber } },
  );
};

export const updatePointOfContact = async (pointOfContact, vatnumber) => {
  return instances.servicesInstance.put(
    'portal/notification/contacts',
    pointOfContact,
    { headers: { vatNumber: vatnumber } },
  );
};

export const activatePointOfContact = async (token) => {
  return ENABLE_MOCKS
    ? activatePointOfContactMock(token)
    : instances.authInstance.get(AUTH_POC_URL);
};

export const createSpecialAgreement = async (
  type,
  createAs,
  agreements,
  referenceNumber,
  reason = 'Pending_Confirmation_Confirm_Party',
) => {
  return instances.servicesInstance.post(
    'portal/self-billing/v1/agreements',
    agreements.map((agreement) => ({
      agreementType: type,
      createAs: createAs,
      buyerId: agreement.buyerVatNumber,
      sellerId: agreement.sellerVatNumber,
      thirdPartyId: agreement.thirdPartyVatNumber
        ? agreement.thirdPartyVatNumber
        : agreement.thirdPartyTinNumber,
      status: 'Open',
      reason: reason,
      details: agreement.note,
      startDate: agreement.agreementStartDate,
      endDate: agreement.agreementEndDate,
      referenceNumber: referenceNumber ?? agreement.referenceNumber,
      buyerName: agreement.buyerName,
      buyerEmail: agreement.buyerEmail,
      buyerPhoneNumber: agreement.buyerPhoneNumber,
      sellerPhoneNumber: agreement.sellerPhoneNumber,
      sellerName: agreement.sellerName,
      sellerEmail: agreement.sellerEmail,
      thirdPartyName: agreement.thirdPartyName,
      thirdPartyEmail: agreement.thirdPartyEmail,
      thirdPhoneNumber: agreement.thirdPartyPhoneNumber,
      thirdPartyTinNumber: agreement.thirdPartyTinNumber,
      agreementFilesDtoList:
        agreement?.agreementDocument?.map((file) => ({
          fileId: file.fileId,
          fileName: file.name,
          fileUrl: file.url,
          fileSize: file.size,
        })) ?? [],
      version: 1,
    })),
  );
};

export const deleteDraftInvoice = async (uuid, vatnumber) => {
  return instances.servicesInstance.delete(`sme/invoices/${uuid}/draft`, {
    headers: { vatNumber: vatnumber },
  });
};

export const getSpecialAgreementInfo = async (agreementId) => {
  return ENABLE_MOCKS
    ? { data: getSpecialAgreementInfoMock(agreementId) }
    : instances.servicesInstance.get(
        'portal/self-billing/v1/agreements/' + agreementId,
      );
};

export const getSpecialAgreementDrafts = async (agreementReference) => {
  return ENABLE_MOCKS
    ? { data: [getSpecialAgreementInfoMock(agreementReference)] }
    : instances.servicesInstance.get(
        'portal/self-billing/v1/agreements/draft?referenceNumber=' +
          agreementReference,
      );
};

export const approveSpecialAgreement = async (agreementId) => {
  return instances.servicesInstance.post(
    `portal/self-billing/v1/agreements/${agreementId}/approvals`,
  );
};

export const rejectSpecialAgreement = async (agreementId, reason) => {
  return instances.servicesInstance.post(
    `portal/self-billing/v1/agreements/${agreementId}/rejections`,
    {
      rejectionReason: reason,
    },
  );
};

export const submitAmendSpecialAgreement = async (agreementId, values) => {
  return instances.servicesInstance.post(
    `portal/self-billing/v1/agreements/${agreementId}/amendments`,
    {
      ...values,
      newStartDate: values.agreementStartDate,
      newEndDate: values.agreementEndDate,
      newDetails: values.note,
      agreementFilesDtoList: values?.agreementDocument?.map((file) => ({
        fileId: file.fileId,
        fileName: file.name,
        fileUrl: file.url,
        fileSize: file.size,
      })),
    },
  );
};

export const getSpecialAgreementAmendments = async (agreementId) => {
  return instances.servicesInstance.get(
    `portal/self-billing/v1/agreements/${agreementId}/amendments`,
  );
};

export const approveSpecialAgreementAmendment = async (
  agreementId,
  amendmentId,
) => {
  return instances.servicesInstance.post(
    `portal/self-billing/v1/agreements/${agreementId}/amendments/${amendmentId}/approvals`,
  );
};

export const rejectSpecialAgreementAmendment = async (
  agreementId,
  amendmentId,
  reason,
) => {
  return instances.servicesInstance.post(
    `portal/self-billing/v1/agreements/${agreementId}/amendments/${amendmentId}/rejections`,
    {
      rejectionReason: reason,
    },
  );
};

export const uploadSpecialAgreementFile = async (file, fileName, extension) => {
  return instances.servicesInstance.post(
    'portal/self-billing/v1/agreements/saveFiles',
    {
      file: file,
      fileName: fileName,
      extension: extension,
    },
  );
};

export const reportTechnicalFailure = async (values) => {
  return instances.servicesInstance.post(
    'portal/notification/api/v1/failure-notifications',
    {
      vatPayerName: values.solutionProviderName,
      failureType: values.faliureType,
      additionalInfo: values.technicalFailureDescription,
      solutionProviderName: values.solutionProviderName,
      failureDate: values.technicalFailureDate,
      failureTime: values.technicalFailureTime,
      expectedResolutionDate: values.expectedResolutionDate,
      ...(values.supportingDocuments?.length > 0 && {
        supportingDocuments: values.supportingDocuments,
      }),
      termsAccepted: true,
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export const resolveTechnicalFailure = async (values) => {
  return instances.servicesInstance.post(
    `portal/notification/api/v1/failure-notifications/${values.failureNotificationId}/resolve`,
    {
      resolutionDate: values.resolutionDate,
      resolutionTime: values.resolutionTime,
      additionalInformation: values.additionalDetails,
      ...(values.supportingDocuments?.length > 0 && {
        supportingDocuments: values.supportingDocuments,
      }),
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export const getVatInfo = async (vatNumber) => {
  return ENABLE_MOCKS
    ? new Promise((resolve) => {
        setTimeout(() => {
          resolve({ data: getVatInfoMock() });
        }, 5000);
      })
    : instances.servicesInstance.get(
        'portal/self-billing/v1/agreements/getVatInfo/' + vatNumber,
      );
};

export const getInvoiceViewSales = async ({ startDate, endDate }) => {
  return ENABLE_MOCKS
    ? invoiceViewSales()
    : instances.servicesInstance.post('portal/invoice-view/api/v1/getSales', {
        startDate,
        endDate,
      });
};

export const getInvoiceViewSalesCounts = async ({ startDate, endDate }) => {
  return ENABLE_MOCKS
    ? invoiceViewSalesCounts()
    : instances.servicesInstance.post(
        'portal/invoice-view/api/v1/getInvoiceStatusSalesCounter',
        {
          startDate,
          endDate,
        },
      );
};

export const getInvoiceViewPurchases = async ({ startDate, endDate }) => {
  return ENABLE_MOCKS
    ? invoiceViewPurchases()
    : instances.servicesInstance.post(
        'portal/invoice-view/api/v1/getPurchases',
        {
          startDate,
          endDate,
        },
      );
};

export const getInvoiceViewPurchasesCounts = async ({ startDate, endDate }) => {
  return ENABLE_MOCKS
    ? invoiceViewPurchasesCounts()
    : instances.servicesInstance.post(
        'portal/invoice-view/api/v1/getInvoiceStatusPurchasesCounter',
        {
          startDate,
          endDate,
        },
      );
};

export const getInvoiceViewInvoiceList = async (
  { view, vatNumber },
  filters,
) => {
  let filterBody = {};

  if (filters) {
    filterBody = filters;
  }
  return ENABLE_MOCKS
    ? invoiceViewInvoiceList()
    : instances.servicesInstance.post(
        `portal/invoice-view/api/v1/invoices?view=${view}&vatNumber=${vatNumber}`,
        filterBody,
      );
};

export const getStatementStatus = async (vatNumber) => {
  return ENABLE_MOCKS
    ? getStatementStatusMock()
    : instances.servicesInstance.get(
        'portal/invoice-view/api/v1/requestStatement/status',
        {
          headers: {
            vatNumber: vatNumber,
          },
        },
      );
};

export const requestStatement = async (
  { startDate, endDate, fields, email, typeOfView },
  vatNumber,
) => {
  return instances.servicesInstance.post(
    'portal/invoice-view/api/v1/requestStatement',
    {
      startDate,
      endDate,
      fields,
      email,
      typeOfView,
    },
    {
      headers: {
        vatNumber: vatNumber,
        'X-Overwrite-Existing': true,
      },
    },
  );
};

export const getInvoiceViewThirdparty = async ({ startDate, endDate }) => {
  return ENABLE_MOCKS
    ? invoiceViewThirdparty()
    : instances.servicesInstance.post(
        'portal/invoice-view/api/v1/getThirdPartyGraphs',
        {
          startDate,
          endDate,
        },
      );
};

export const getInvoiceThirdPartyStats = async ({ startDate, endDate }) => {
  return ENABLE_MOCKS
    ? invoiceViewThirdpartyStats()
    : instances.servicesInstance.post(
        'portal/invoice-view/api/v1/getThirdParty',
        {
          startDate,
          endDate,
        },
      );
};

export const getInvoiceViewThirdpartyCounts = async ({
  startDate,
  endDate,
}) => {
  return ENABLE_MOCKS
    ? invoiceViewThirdpartyCounts()
    : instances.servicesInstance.post(
        'portal/invoice-view/api/v1/getInvoiceStatusThirdPartyCounter',
        {
          startDate,
          endDate,
        },
      );
};

export const getAssetUrl = async (assetId) => {
  return instances.servicesInstance.get(
    `portal/authentication/auth/getCatalog?assetId=${assetId}`,
  );
};

export const logoutUser = async () => {
  return instances.servicesInstance.post('portal/authentication/token/logout', {
    token: localStorage.getItem('auth_token'),
    refreshToken: localStorage.getItem('auth_refresh_token'),
  });
};

export const getFileDownloadUrl = async (file) => {
  return instances.servicesInstance.post(
    `portal/self-billing/v1/agreements/downloadFile?url=${file}`,
  );
};
